import React, { useState, useRef, useEffect, useCallback, lazy, useMemo } from "react";
import "../styles/training.css";
import {
    initCamera,
    validateRecordingSettings,
    handleRecordingProcess,
    fetchGestureCounts,
    fetchUsers,
    clearVideoMetadataCache,
    fetchSampleVideoUrl,
    fetchSampleGestures,
    ORIENTATION_INSTRUCTIONS,
    fetchUserSettings,
    saveUserSettings
} from "../utils/TrainingHelpers";
import VideoList from "../components/Training/VideoList";
import RecordingControls from "../components/Training/RecordingControls";
import RecordingSettings from "../components/Training/RecordingSettings";
import VideoPlayback from "../components/Training/VideoPlayback";
import SampleGesturePopup from "../components/Training/SampleGesturePopup";
import SettingsPanel from "../components/Training/SettingsPanel";
import { jwtDecode } from 'jwt-decode';
import { categories, formatGestureForDisplay } from "../utils/GestureCategories";
import { FaQuestion } from "react-icons/fa";

const RecordingStatusIndicator = lazy(() => import("../components/Training/RecordingStatusIndicator"));
const OrientationCountdownOverlay = lazy(() => import("../components/Training/OrientationCountdownOverlay"));

const DEFAULT_SHORT_COUNTDOWN = 2;
const DEFAULT_LONG_COUNTDOWN = 5;

// Default workflow counts for most categories (22 recordings)
const WORKFLOW_STEPS_22 = [
    { instruction: "Face the camera (normal speed & distance)", count: 5, orientation: 'front' },
    { instruction: "Turn your body to your left (will appear right on screen)", count: 3, orientation: 'left' },
    { instruction: "Turn your body to your right (will appear left on screen)", count: 3, orientation: 'right' },
    { instruction: "Perform a bit slower", count: 3, orientation: 'slow' },
    { instruction: "Perform a bit faster", count: 3, orientation: 'fast' },
    { instruction: "Move a bit closer to the camera", count: 3, orientation: 'close' },
    { instruction: "Move a bit further from the camera", count: 2, orientation: 'far' }
];

// Reduced workflow for early units (20 recordings)
const WORKFLOW_STEPS_20 = [
    { instruction: "Face the camera (normal speed & distance)", count: 5, orientation: 'front' },
    { instruction: "Turn your body to your left (will appear right on screen)", count: 3, orientation: 'left' },
    { instruction: "Turn your body to your right (will appear left on screen)", count: 3, orientation: 'right' },
    { instruction: "Perform a bit slower", count: 2, orientation: 'slow' },
    { instruction: "Perform a bit faster", count: 3, orientation: 'fast' },
    { instruction: "Move a bit closer to the camera", count: 2, orientation: 'close' },
    { instruction: "Move a bit further from the camera", count: 2, orientation: 'far' }
];

const ADRIAN_WORKFLOW_STEPS = [
    { instruction: "Face the camera (normal speed & distance)", count: 2, orientation: 'front' },
    { instruction: "Turn your body to your left (will appear right on screen)", count: 2, orientation: 'left' },
    { instruction: "Turn your body to your right (will appear left on screen)", count: 2, orientation: 'right' },
    { instruction: "Perform a bit slower", count: 1, orientation: 'slow' },
    { instruction: "Perform a bit faster", count: 1, orientation: 'fast' },
    { instruction: "Move a bit closer to the camera", count: 1, orientation: 'close' },
    { instruction: "Move a bit further from the camera", count: 1, orientation: 'far' }
];

const Training = () => {
    const [gesture, setGesture] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [videos, setVideos] = useState([]);
    const [error, setError] = useState("");
    const [showCamera, setShowCamera] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [stream, setStream] = useState(null);
    const [selectedVideos, setSelectedVideos] = useState(new Set());
    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState(new Set());
    const [tempFilters, setTempFilters] = useState(new Set());
    const [, setCurrentStepCount] = useState(0);
    const [, setCurrentStepTotal] = useState(0);
    const [overallCount, setOverallCount] = useState(0);
    const [currentInstruction, setCurrentInstruction] = useState("");
    const [countdown, setCountdown] = useState(0);
    const [isLongCountdownActive, setIsLongCountdownActive] = useState(false);
    const [currentOrientation, setCurrentOrientation] = useState("");
    const currentBatchIdRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [gestureError, setGestureError] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isBatchActive, setIsBatchActive] = useState(false);
    const isPausedRef = useRef(false);
    const [isFirewallMode] = useState(false);
    const [sampleVideoUrl, setSampleVideoUrl] = useState(null);
    const [showSamplePopup, setShowSamplePopup] = useState(false);
    const [showHelpHint, setShowHelpHint] = useState(false);
    const helpHintTimerRef = useRef(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isAdrian, setIsAdrian] = useState(false);
    const [currentUserId, setCurrentUserId] = useState('');
    const [viewAllUsers, setViewAllUsers] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [sampleGestures, setSampleGestures] = useState(null);
    const [gestureCounts, setGestureCounts] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const filterRef = useRef(null);
    const videoRef = useRef(null);
    const currentBatchRecordingsRef = useRef([]);
    const videoListRef = useRef(null);
    const [shortCountdownLength, setShortCountdownLength] = useState(DEFAULT_SHORT_COUNTDOWN);
    const [longCountdownLength, setLongCountdownLength] = useState(DEFAULT_LONG_COUNTDOWN);
    const [showSettings, setShowSettings] = useState(false);
    const [showNextGestureButton, setShowNextGestureButton] = useState(false);
    const lastGestureRef = useRef("");
    const [showAllGestures, setShowAllGestures] = useState(false);

    // Load persisted user settings on mount
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settings = await fetchUserSettings();
                if (settings.preferredShortCountdown) {
                    setShortCountdownLength(settings.preferredShortCountdown);
                }
                if (settings.preferredLongCountdown) {
                    setLongCountdownLength(settings.preferredLongCountdown);
                }
            } catch (err) {
                console.error('Error fetching user settings:', err);
            }
        };
        loadSettings();
    }, []);

    const availableGestures = useMemo(() => {
        if (!selectedCategory) return [];
        const cat = categories.find((c) => c.id === selectedCategory);
        if (!cat) return [];
        return cat.gestures.filter((g) => {
            const allowedBySample = sampleGestures === null || sampleGestures.includes(g);
            const notRecorded = (gestureCounts[g] || 0) === 0;
            return allowedBySample && notRecorded;
        });
    }, [selectedCategory, sampleGestures, gestureCounts]);

    const filteredGestures = useMemo(() => {
        if (!selectedCategory) return [];
        const cat = categories.find((c) => c.id === selectedCategory);
        if (!cat) return [];
        return cat.gestures.filter((g) => {
            return sampleGestures === null || sampleGestures.includes(g);
        });
    }, [selectedCategory, sampleGestures]);

    const nextGestureDisabled = availableGestures.length === 0;

    useEffect(() => {
        if (gesture) {
            lastGestureRef.current = gesture;
        }
    }, [gesture]);


    // Add click outside handler for filter popup
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setShowFilterPopup(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const [workflowSteps, setWorkflowSteps] = useState(WORKFLOW_STEPS_22);
    const [replacementSteps, setReplacementSteps] = useState(null);
    const [preAssignedBatchId, setPreAssignedBatchId] = useState(null);
    const [lockedBatchId, setLockedBatchId] = useState(null);

    const resetReplacementMode = useCallback(() => {
        setReplacementSteps(null);
        setPreAssignedBatchId(null);
        setLockedBatchId(null);
    }, []);

    const cancelReplacementMode = useCallback(() => {
        setReplacementSteps(null);
        setPreAssignedBatchId(null);
        setLockedBatchId(null);
        setSelectedCategory("");
        setGesture("");
    }, []);

    const beginReplacement = useCallback((batchId, gestureName, categoryId, missing) => {
        setSelectedCategory(categoryId);
        setGesture(gestureName);
        setGestureError(false);
        setSampleVideoUrl(null);
        setShowSamplePopup(false);

        const steps = missing.map(m => ({
            instruction: ORIENTATION_INSTRUCTIONS[m.orientation],
            count: m.count,
            orientation: m.orientation
        }));
        setReplacementSteps(steps);
        setPreAssignedBatchId(batchId);
        setLockedBatchId(batchId);
    }, []);

    const batchTotal = useMemo(() => workflowSteps.reduce((sum, s) => sum + s.count, 0), [workflowSteps]);

    useEffect(() => {
        const token = localStorage.getItem('jwt');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setIsAdmin(decodedToken.role === 'admin');

                const userId = decodedToken.id || decodedToken.username;
                setCurrentUserId(userId);
                if (userId === 'adrian') {
                    setIsAdrian(true);
                    setWorkflowSteps(ADRIAN_WORKFLOW_STEPS);
                }
            } catch (error) {
                console.error('Error decoding token:', error);
            }
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!isAdmin) {
            fetchSampleGestures()
                .then((gestures) => {
                    if (!cancelled) {
                        setSampleGestures(gestures);
                    }
                })
                .catch((err) => {
                    if (!cancelled) {
                        console.error('Error fetching sample gestures:', err);
                    }
                });
        } else {
            setSampleGestures(null);
        }

        return () => {
            cancelled = true;
        };
    }, [isAdmin]);

    useEffect(() => {
        const loadUsers = async () => {
            if (viewAllUsers && isAdmin) {
                try {
                    const list = await fetchUsers();
                    setUsers(list);
                } catch (err) {
                    console.error('Error fetching users:', err);
                }
            } else {
                setUsers([]);
                setSelectedUser('');
            }
        };
        loadUsers();
    }, [viewAllUsers, isAdmin]);

    // Separate useEffect for loading videos to avoid race conditions
    useEffect(() => {
        if (showCamera) {
            initCamera(videoRef, setStream, setError);
        }
    }, [showCamera]);

    // Cleanup help hint timer on unmount
    useEffect(() => {
        return () => {
            if (helpHintTimerRef.current) {
                clearTimeout(helpHintTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [stream]);

    // Update isPausedRef whenever isPaused changes
    useEffect(() => {
        isPausedRef.current = isPaused;
    }, [isPaused]);

    // Switch workflow steps based on selected category or replacement mode
    useEffect(() => {
        if (replacementSteps) {
            setWorkflowSteps(replacementSteps);
            return;
        }
        if (isAdrian) {
            setWorkflowSteps(ADRIAN_WORKFLOW_STEPS);
            return;
        }
        if (["unit1", "unit2", "unit3", "letters", "numbers", "numberingSystems"].includes(selectedCategory)) {
            setWorkflowSteps(WORKFLOW_STEPS_20);
        } else {
            setWorkflowSteps(WORKFLOW_STEPS_22);
        }
    }, [selectedCategory, isAdrian, replacementSteps]);

    const countdownTimerRef = useRef(null);

    const startCountdown = useCallback((length, isLong = false) => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }

        setCountdown(length);
        setIsUploading(false);
        setIsLongCountdownActive(isLong);

        return new Promise(resolve => {
            countdownTimerRef.current = setInterval(() => {
                if (isPausedRef.current) return;

                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownTimerRef.current);
                        countdownTimerRef.current = null;
                        if (isLong) setIsLongCountdownActive(false);
                        resolve();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        });
    }, []);

    useEffect(() => {
        return () => {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }
        };
    }, []);

    const updateGestureCounts = useCallback((gesture, increment, newCounts = null, newTotal = null) => {
        if (newCounts !== null && newTotal !== null) {
            setGestureCounts(newCounts);
            setTotalCount(newTotal);
        } else if (gesture) {
            setGestureCounts(prevCounts => {
                const newCounts = { ...prevCounts };
                newCounts[gesture] = (newCounts[gesture] || 0) + increment;
                return newCounts;
            });
            setTotalCount(prevTotal => prevTotal + increment);
        }
    }, []);

    useEffect(() => {
        const loadCounts = async () => {
            try {
                const data = await fetchGestureCounts(viewAllUsers, selectedUser || null, 'training');
                updateGestureCounts(null, 0, data.counts, data.total);
            } catch (err) {
                console.error("Error fetching gesture counts:", err);
                setError("Failed to fetch gesture counts");
            }
        };

        loadCounts();
    }, [viewAllUsers, selectedUser, updateGestureCounts]);

    // Reset selected gesture if the user has already recorded it
    // Skip this when recording replacements or when showing all gestures
    useEffect(() => {
        if (!replacementSteps && !showAllGestures && gesture && gestureCounts[gesture] > 0) {
            setGesture("");
        }
    }, [gestureCounts, gesture, replacementSteps, showAllGestures]);

    const startNextRecording = useCallback(async (count, stepTotal, orientation, onComplete, skipInitial = false) => {
        await handleRecordingProcess(
            count,
            stepTotal,
            currentBatchIdRef.current,
            stream,
            () => startCountdown(shortCountdownLength),
            setIsRecording,
            setCurrentStepCount,
            setIsUploading,
            setError,
            setVideos,
            gesture,
            isFirewallMode,
            currentBatchRecordingsRef,
            viewAllUsers,
            updateGestureCounts,
            orientation,
            skipInitial,
            () => setOverallCount(prev => prev + 1),
            'training',
            onComplete
        );
    }, [
        stream,
        gesture,
        isFirewallMode,
        viewAllUsers,
        updateGestureCounts,
        setIsRecording,
        setCurrentStepCount,
        setIsUploading,
        setError,
        setVideos,
        setOverallCount,
        startCountdown,
        shortCountdownLength
    ]);

    const startRecording = useCallback(async () => {
        const hasError = validateRecordingSettings(
            gesture,
            stream,
            setError,
            setGestureError
        );

        if (hasError) {
            return;
        }

        setGestureError(false);
        setError("");
        setIsRecording(true);
        setShowNextGestureButton(false);

        const batchId = preAssignedBatchId || `${Date.now()}`;
        currentBatchIdRef.current = batchId;
        if (preAssignedBatchId) setPreAssignedBatchId(null);
        setLockedBatchId(null);
        currentBatchRecordingsRef.current = [];
        setIsBatchActive(true);
        setOverallCount(0);

        const startWorkflow = async (stepIndex) => {
            if (stepIndex >= workflowSteps.length) {
                setIsRecording(false);
                setCurrentStepCount(0);
                setCurrentStepTotal(0);
                setCurrentInstruction("");
                setCurrentOrientation("");
                setIsBatchActive(false);
                setShowNextGestureButton(true);
                if (videoListRef.current) {
                    if (videoListRef.current.refreshVisibleBatches) {
                        videoListRef.current.refreshVisibleBatches();
                    }
                    if (videoListRef.current.refreshBatch) {
                        videoListRef.current.refreshBatch(currentBatchIdRef.current);
                    }
                }
                resetReplacementMode();
                return;
            }

            const step = workflowSteps[stepIndex];
            setCurrentStepTotal(step.count);
            setCurrentStepCount(0);
            setCurrentInstruction(step.instruction);
            setCurrentOrientation(step.orientation);

            await startCountdown(longCountdownLength, true);

            await startNextRecording(0, step.count, step.orientation, () => startWorkflow(stepIndex + 1), true);
        };

        startWorkflow(0);
    }, [
        gesture,
        stream,
        workflowSteps,
        startNextRecording,
        startCountdown,
        longCountdownLength,
        preAssignedBatchId,
        resetReplacementMode
    ]);

    const showHintTemporarily = () => {
        setShowHelpHint(true);
        clearTimeout(helpHintTimerRef.current);
        helpHintTimerRef.current = setTimeout(() => setShowHelpHint(false), 3000);
    };

    const handleClosePopup = () => {
        setShowSamplePopup(false);
        showHintTemporarily();
    };

    const handleSaveSettings = async (settings) => {
        try {
            await saveUserSettings(settings);
        } catch (err) {
            console.error('Error saving settings:', err);
        }
    };

    const handleStartFromPopup = useCallback(() => {
        setShowSamplePopup(false);
        startRecording();
    }, [setShowSamplePopup, startRecording]);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.code === 'Space' && showCamera) {
                e.preventDefault();
                if (showSamplePopup) {
                    handleStartFromPopup();
                } else if (!isRecording && !isUploading) {
                    setGestureError(false);
                    if (showSamplePopup) {
                        setShowSamplePopup(false);
                    }
                    startRecording();
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [showCamera, isRecording, isUploading, gesture, showSamplePopup, startRecording, handleStartFromPopup]);

    const handleGestureChange = async (e) => {
        if (!isRecording) {
            resetReplacementMode();
            const value = e.target.value;
            setGesture(value);
            setGestureError(false);
            setShowNextGestureButton(false);
            try {
                const url = await fetchSampleVideoUrl(value);
                setSampleVideoUrl(url);
                setShowSamplePopup(true);
            } catch (err) {
                console.error('Error fetching sample video:', err);
                setSampleVideoUrl(null);
                setShowSamplePopup(false);
            }
        }
    };

const handleCategoryChange = (e) => {
        if (!isRecording) {
            resetReplacementMode();
            setSelectedCategory(e.target.value);
            setGesture("");
            setGestureError(false);
            setSampleVideoUrl(null);
            setShowSamplePopup(false);
            setShowNextGestureButton(false);
        }
    };

    const handleSelectNextGesture = useCallback(async () => {
        if (nextGestureDisabled) return;
        if (filteredGestures.length === 0) return;

        const currentIndex = filteredGestures.indexOf(lastGestureRef.current);
        let nextIndex = currentIndex >= 0 ? (currentIndex + 1) % filteredGestures.length : 0;

        let nextGesture = filteredGestures[nextIndex];
        let attempts = 0;
        while (gestureCounts[nextGesture] > 0 && attempts < filteredGestures.length) {
            nextIndex = (nextIndex + 1) % filteredGestures.length;
            nextGesture = filteredGestures[nextIndex];
            attempts += 1;
        }

        if (gestureCounts[nextGesture] > 0) return;

        setGesture(nextGesture);
        setGestureError(false);
        setShowNextGestureButton(false);
        try {
            const url = await fetchSampleVideoUrl(nextGesture);
            setSampleVideoUrl(url);
            setShowSamplePopup(true);
        } catch (err) {
            console.error('Error fetching sample video:', err);
            setSampleVideoUrl(null);
            setShowSamplePopup(false);
        }
    }, [nextGestureDisabled, filteredGestures, gestureCounts]);



    const handleBackToCamera = () => {
        setSelectedVideo(null);
        setShowCamera(true);
    };

    // Update effect to handle video container sizing
    useEffect(() => {
        const videoContainer = document.querySelector('.training-video-container');
        if (videoContainer) {
            // Use larger spacing by default and when recording/counting down
            const shouldUseLargerSpacing = !selectedCategory || isRecording || countdown > 0;
            videoContainer.style.maxWidth = shouldUseLargerSpacing
                ? 'min(1600px, calc((100vh - 290px) * 16/9))'  // Larger spacing by default and during recording/countdown
                : 'min(1600px, calc((100vh - 340px) * 16/9))';  // Condensed spacing when category selected
        }
    }, [selectedCategory, isRecording, countdown]);

    // Clear video cache on mount
    useEffect(() => {
        clearVideoMetadataCache();
    }, []);

    const handlePauseResume = useCallback(() => {
        if (isPaused) {
            // When resuming, restart the countdown from the current value
            const currentValue = countdown;
            setCountdown(currentValue);
        }
        setIsPaused(prev => !prev);
    }, [countdown, isPaused]);

    const handleAbort = useCallback(() => {
        window.location.reload();
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.code === 'Escape' && isRecording && countdown > 0 && !isUploading) {
                e.preventDefault();
                handleAbort();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isRecording, countdown, isUploading, handleAbort]);

    return (
        <div className="training-container">
            <div className="video-section">
                {showCamera ? (
                    <>
                        <div className={`training-video-container ${isRecording && !isUploading && countdown === 0 ? 'recording-active' : ''}`}>
                            <video ref={videoRef} className="video-preview" autoPlay muted playsInline />
                            <OrientationCountdownOverlay
                                visible={isLongCountdownActive && countdown > 0}
                                countdown={countdown}
                                instruction={currentInstruction}
                                orientation={currentOrientation}
                            />
                            <RecordingStatusIndicator
                                isRecording={isRecording}
                                gesture={gesture}
                                currentCount={overallCount}
                                totalCount={batchTotal}
                                instruction={currentInstruction}
                                isUploading={isUploading}
                                isFirewallMode={isFirewallMode}
                                countdown={countdown}
                                isPaused={isPaused}
                            />
                        </div>
                        <div className="recording-info">
                            {countdown > 0 && (
                                <div className={`countdown ${isPaused ? 'paused' : ''}`}>{countdown}</div>
                            )}
                        </div>
                        <RecordingControls
                            isRecording={isRecording}
                            startRecording={startRecording}
                            isUploading={isUploading}
                            isPaused={isPaused}
                            onPauseResume={handlePauseResume}
                            onAbort={handleAbort}
                            countdown={countdown}
                            onSettingsClick={() => setShowSettings(true)}
                            helpButton={sampleVideoUrl && !showSamplePopup ? (
                                <div className="help-button-container">
                                    <button
                                        className="sample-help-button"
                                        onClick={() => setShowSamplePopup(true)}
                                    >
                                        <FaQuestion style={{ width: '18px', height: '18px' }}/>
                                    </button>
                                    {showHelpHint && <div className="help-hint" />}
                                </div>
                            ) : null}
                        />
                        {!isRecording && !replacementSteps && (
                            <RecordingSettings
                                gesture={gesture}
                                handleGestureChange={handleGestureChange}
                                selectedCategory={selectedCategory}
                                handleCategoryChange={handleCategoryChange}
                                isRecording={isRecording}
                                gestureError={gestureError}
                                isAdmin={isAdmin}
                                showAllGestures={showAllGestures}
                                setShowAllGestures={setShowAllGestures}
                                sampleGestures={sampleGestures}
                                gestureCounts={gestureCounts}
                                onNextGesture={handleSelectNextGesture}
                                nextGestureDisabled={nextGestureDisabled}
                                showNextButton={showNextGestureButton && Boolean(selectedCategory)}
                            />
                        )}
                        {!isRecording && replacementSteps && (
                            <div className="replacement-banner">
                                <span>
                                    Recording {replacementSteps.reduce((a, s) => a + s.count, 0)} replacement(s) for {formatGestureForDisplay(gesture)}
                                </span>
                                <button className="back-button" onClick={cancelReplacementMode}>Back</button>
                            </div>
                        )}
                        {sampleVideoUrl && (
                            <SampleGesturePopup
                                visible={showSamplePopup}
                                gesture={gesture}
                                videoUrl={sampleVideoUrl}
                                onClose={handleClosePopup}
                                onStart={handleStartFromPopup}
                            />
                        )}
                        <SettingsPanel
                            visible={showSettings}
                            longCountdown={longCountdownLength}
                            shortCountdown={shortCountdownLength}
                            setLongCountdown={setLongCountdownLength}
                            setShortCountdown={setShortCountdownLength}
                            onClose={() => setShowSettings(false)}
                            onSave={handleSaveSettings}
                        />
                    </>
                ) : (
                    <VideoPlayback
                        selectedVideo={selectedVideo}
                        handleBackToCamera={handleBackToCamera}
                    />
                )}
                {error && <p className="error-message">{error}</p>}
            </div>

            <VideoList
                videos={videos}
                setVideos={setVideos}
                setError={setError}
                selectedVideo={selectedVideo}
                setSelectedVideo={setSelectedVideo}
                setShowCamera={setShowCamera}
                selectedVideos={selectedVideos}
                setSelectedVideos={setSelectedVideos}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                showFilterPopup={showFilterPopup}
                setShowFilterPopup={setShowFilterPopup}
                tempFilters={tempFilters}
                setTempFilters={setTempFilters}
                gestures={categories}
                isAdmin={isAdmin}
                handleRecordingProcess={startNextRecording}
                viewAllUsers={viewAllUsers}
                setViewAllUsers={setViewAllUsers}
                users={users}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                gestureCounts={gestureCounts}
                totalCount={totalCount}
                updateGestureCounts={updateGestureCounts}
                ref={videoListRef}
                isBatchActive={isBatchActive}
                lockedBatchId={lockedBatchId}
                isAdrian={isAdrian}
                onAddReplacements={beginReplacement}
                currentUserId={currentUserId}
                page="training"
            />
        </div>
    );
};

export default Training;